import React, { useEffect, useRef, useState } from "react";
import { Mic, Square, Send, X, Loader2 } from "lucide-react";
import { uploadToR2 } from "../../lib/studioApi";
import { toast } from "sonner";

function pickMime() {
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/ogg;codecs=opus",
  ];
  for (const m of candidates) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(m)) {
      return m;
    }
  }
  return "";
}

function fmt(secs) {
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60)
    .toString()
    .padStart(2, "0");
  return `${m}:${s}`;
}

const MAX_SECONDS = 180; // 3 min cap

/**
 * VoiceRecorder
 * Compact mic button → recording UI → preview → onSend(attachment).
 * Triggers onSend with a single voice-kind attachment {kind:"voice", url, name, size, contentType}.
 */
export default function VoiceRecorder({ asAdmin = false, onSend, theme = "dark", onPhaseChange }) {
  const supported =
    typeof MediaRecorder !== "undefined" && !!navigator.mediaDevices;
  const [phase, setPhase] = useState("idle"); // idle | recording | preview | uploading
  const [seconds, setSeconds] = useState(0);
  const [blob, setBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (onPhaseChange) onPhaseChange(phase);
  }, [phase, onPhaseChange]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const startRecording = async () => {
    if (!supported) {
      toast.error("Voice recording not supported on this browser");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = pickMime();
      const rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        const type = rec.mimeType || "audio/webm";
        const b = new Blob(chunksRef.current, { type });
        setBlob(b);
        setPreviewUrl(URL.createObjectURL(b));
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
        setPhase("preview");
      };
      rec.start();
      setSeconds(0);
      setPhase("recording");
      timerRef.current = setInterval(() => {
        setSeconds((s) => {
          const next = s + 1;
          if (next >= MAX_SECONDS && recorderRef.current?.state === "recording") {
            recorderRef.current.stop();
            clearInterval(timerRef.current);
          }
          return next;
        });
      }, 1000);
    } catch (e) {
      toast.error("Microphone permission denied");
      setPhase("idle");
    }
  };

  const stopRecording = () => {
    if (recorderRef.current?.state === "recording") {
      recorderRef.current.stop();
    }
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const discard = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setBlob(null);
    setPreviewUrl("");
    setSeconds(0);
    setPhase("idle");
  };

  const sendIt = async () => {
    if (!blob || phase === "uploading") return;
    setPhase("uploading");
    try {
      const ext = blob.type.includes("mp4")
        ? "m4a"
        : blob.type.includes("ogg")
          ? "ogg"
          : "webm";
      const file = new File([blob], `voice-${Date.now()}.${ext}`, { type: blob.type });
      const att = await uploadToR2(file, "voice", { asAdmin });
      onSend({ ...att, kind: "voice" });
      discard();
    } catch (e) {
      toast.error("Voice upload failed");
      setPhase("preview");
    }
  };

  const dark = theme === "dark";
  const surface = dark ? "bg-[#161a2c] border-white/10" : "bg-gray-50 border-gray-200";
  const textMuted = dark ? "text-white/60" : "text-gray-500";

  if (phase === "idle") {
    return (
      <button
        type="button"
        data-testid="voice-record-btn"
        onClick={startRecording}
        disabled={!supported}
        title={supported ? "Record voice message" : "Recording not supported"}
        className={`h-11 w-11 rounded-xl border flex-shrink-0 flex items-center justify-center transition-colors ${
          dark
            ? "border-white/10 text-white/70 hover:text-paul-yellow hover:border-paul-yellow/60"
            : "border-gray-200 text-gray-500 hover:text-paul-yellow hover:border-paul-yellow"
        } disabled:opacity-40`}
      >
        <Mic className="w-5 h-5" />
      </button>
    );
  }

  if (phase === "recording") {
    return (
      <div
        data-testid="voice-recording-row"
        className={`flex-1 flex items-center gap-3 rounded-xl border px-3 h-11 ${surface}`}
      >
        <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse flex-shrink-0" />
        <span className={`text-sm font-mono ${dark ? "text-white" : "text-navy"}`}>
          {fmt(seconds)}
        </span>
        <span className={`text-xs ${textMuted} hidden sm:inline`}>Recording…</span>
        <div className="flex-1" />
        <button
          type="button"
          onClick={discard}
          className={`text-xs ${textMuted} hover:text-red-400`}
        >
          Cancel
        </button>
        <button
          type="button"
          data-testid="voice-stop-btn"
          onClick={stopRecording}
          className="h-8 w-8 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600"
          title="Stop"
        >
          <Square className="w-3.5 h-3.5 fill-current" />
        </button>
      </div>
    );
  }

  // preview / uploading
  return (
    <div
      data-testid="voice-preview-row"
      className={`flex-1 min-w-0 flex items-center gap-2 rounded-xl border px-2 sm:px-3 h-11 ${surface}`}
    >
      <audio
        ref={audioRef}
        src={previewUrl}
        controls
        className="h-8 flex-1 min-w-0"
      />
      <span className={`text-xs font-mono ${textMuted} hidden sm:inline flex-shrink-0`}>
        {fmt(seconds)}
      </span>
      <button
        type="button"
        onClick={discard}
        disabled={phase === "uploading"}
        title="Discard"
        aria-label="Discard recording"
        className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 disabled:opacity-40 ${
          dark
            ? "text-white/60 hover:text-red-400 hover:bg-white/5"
            : "text-gray-500 hover:text-red-500 hover:bg-gray-100"
        }`}
      >
        <X className="w-4 h-4" />
      </button>
      <button
        type="button"
        data-testid="voice-send-btn"
        onClick={sendIt}
        disabled={phase === "uploading"}
        aria-label="Send voice note"
        className="h-9 w-9 sm:h-8 sm:w-auto sm:px-3 rounded-full bg-paul-yellow text-navy font-bold text-sm flex items-center justify-center sm:gap-1.5 flex-shrink-0"
        style={{ color: "#0b1535" }}
      >
        {phase === "uploading" ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Send className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
        )}
        <span className="hidden sm:inline">Send</span>
      </button>
    </div>
  );
}
